/**
 * PermittingEntry — the client's PERMITTING card (docs/OPERATING_MODEL.md, build step 4 slice B;
 * Matt, 2026-09-23). One living card per project, compiled on every load from the Permits section's
 * activity log (api/_permittingJourney.js#fetchProjectPermitting, attached as entry.permitting): one
 * section per agency review, every logged activity with the team's memo exactly as written (the team
 * writes memos for the client), and what's next with its best-guess date. Read-only.
 *
 * The expected date is worded honestly: "the agency's estimate" only when the agency gave it, "our
 * estimate" otherwise (recorded when the date was entered).
 */

import Markdown from '../../components/Markdown.jsx'
import { J } from '../journeyUI.jsx'

const fmt = (ymd) => ymd ? new Date(ymd + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''

function Review({ r }) {
  const title = [r.agency, r.permitType].filter(Boolean).join(' · ') || 'Agency review'
  return (
    <div style={{ padding: '16px 0', borderBottom: '0.5px solid rgba(7,39,45,0.08)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 14.5, fontWeight: 600, color: '#07272D' }}>{title}</span>
        {r.approved
          ? <span style={pill('#1D9E75')}>Approved</span>
          : <span style={pill('#c79a3a')}>In review</span>}
      </div>
      {r.trackingNumber && <div style={{ ...J.mono, fontSize: 11, marginTop: 2 }}>Agency no. {r.trackingNumber}</div>}
      <div style={{ margin: '10px 0 0 4px', borderLeft: '0.5px solid rgba(7,39,45,0.2)', paddingLeft: 14 }}>
        {r.activities.map(a => (
          <div key={a.id} style={{ padding: '6px 0' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
              <span style={{ ...J.mono, fontSize: 11.5, minWidth: 92 }}>{fmt(a.date)}</span>
              <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13.5, fontWeight: 500, color: '#07272D' }}>{a.label}</span>
            </div>
            {a.memo && <div style={{ ...J.body, fontSize: 13, margin: '3px 0 0 102px' }}><Markdown text={a.memo} /></div>}
          </div>
        ))}
        {r.next && (
          <div style={{ marginTop: 6, padding: '8px 12px', background: 'rgba(7,39,45,0.04)', borderRadius: 6 }}>
            <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13.5, color: '#07272D' }}>
              <span style={{ ...J.mono, fontSize: 11.5, marginRight: 10 }}>Next</span>
              {r.next.label}{r.next.date ? ', expected around ' + fmt(r.next.date) : ''}
            </div>
            {r.next.date && (
              <div style={{ ...J.body, fontSize: 12, marginTop: 2 }}>
                {r.next.source === 'them'
                  ? 'The agency estimates this date. Reviews sometimes run long; we will update you if it moves.'
                  : 'Our estimate, based on past reviews. We will update you if it moves.'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function pill(color) {
  return { fontFamily: '"DM Sans", sans-serif', fontSize: 10.5, padding: '2px 9px', borderRadius: 20, border: '0.5px solid ' + color + '55', color, whiteSpace: 'nowrap' }
}

export default function PermittingEntry({ entry }) {
  const p = entry.permitting || { reviews: [] }
  return (
    <div>
      <div style={J.eyebrow}>Permitting</div>
      <h2 style={J.title}>{entry.title || 'Permitting'}</h2>
      <hr style={J.rule} />
      <div style={J.body}>Where your project stands with each agency. We update this as each step happens.</div>
      {p.reviews.length === 0
        ? <div style={{ ...J.mono, marginTop: 16 }}>Nothing to report yet.</div>
        : <div style={{ marginTop: 8 }}>{p.reviews.map(r => <Review key={r.id} r={r} />)}</div>}
    </div>
  )
}
